// @flow
import React, { useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import pipe from 'ramda/es/pipe'
import filter from 'ramda/es/filter'
import keys from 'ramda/es/keys'
import find from 'ramda/es/find'

import PlainNav, { NavItem } from 'app/common/ui/PlainNav'
import { DotsLoader } from 'app/common/components/Loader'
import { fetchSections } from 'app/actions/cafeteria/sections'
import isProduction from 'app/common/lib/isProduction'

import styles from './Navigation.scss'


type Props = {
  baseURL: string
  studentID: string
  pathname: string
  push: (path: string) => void
  isCamp: boolean
}

const PoorNavigation = ({
  baseURL,
  studentID,
  pathname,
  push,
  isCamp,
}: Props) => {
  const { t } = useTranslation('cafeteria')
  const dispatch = useDispatch()
  const financesActive = /^\/apps\/(\d+)\/cafeteria\/paymentOrders$/.test(window.location.pathname)

  const {
    free: freeSections,
    loading,
    result,
  } = useSelector((state: any) => state.cafeteria.sections, shallowEqual)

  const loadingFreeSections = loading && result !== 'success'

  useEffect(() => {
    dispatch(fetchSections(parseInt(studentID, 10)))
  }, [dispatch, studentID])

  const redirectIfNotAllowed = useCallback(() => {
    if (loadingFreeSections) return
    const notAllowedSections = pipe(
      filter(state => state === false),
      keys,
    )(freeSections)
    if (find(section => pathname.startsWith(`${baseURL}/${section}`), notAllowedSections)) {
      push(`${baseURL}/menu`)
    }
  }, [baseURL, freeSections, loadingFreeSections, pathname, push])

  useEffect(() => {
    redirectIfNotAllowed()
  }, [freeSections]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loadingFreeSections) {
    return (
      <DotsLoader />
    )
  }

  return (
    // div здесь нужен, для нормального функционирования прилипания
    <div className={styles.root}>
      <PlainNav className={styles.mainMenu}>
        {freeSections.menu && <NavItem to={`${baseURL}/menu`}>
          {t('nav_menu', 'Меню')}
        </NavItem>}
        {freeSections.orders && !isCamp && <NavItem to={`${baseURL}/orders`}>
          {t('nav_orders', 'Заказы')}
        </NavItem>}
        {freeSections.order_requests && <NavItem to={`${baseURL}/orderRequests`}>
          {isCamp
            ? t('nav_order_requests_camp', 'Заявки на лагерь')
            : t('nav_order_requests', 'Заявки на класс')}
        </NavItem>}
        {freeSections.purchases && !isCamp && <NavItem to={`${baseURL}/purchases`}>
          {t('nav_purchases', 'Покупки')}
        </NavItem>}
        {freeSections.schedule && !isCamp && <NavItem to={`${baseURL}/schedule`}>
          {t('nav_schedule', 'График питания')}
        </NavItem>}
        {freeSections.finances && <NavItem to={`${baseURL}/finances`} active={financesActive}>
          {t('nav_finances', 'Финансы')}
        </NavItem>}
        {freeSections.subsidies && <NavItem to={`${baseURL}/subsidies`}>
          {t('nav_subsidies', 'Дотации')}
        </NavItem>}
        {!isProduction()
          && <NavItem to={`${baseURL}/compensation`}>
            {t('nav_compensation', 'Компенсация')}
          </NavItem>}
        {freeSections.settings && <NavItem to={`${baseURL}/settings`}>
          {t('nav_settings', 'Настройки')}
        </NavItem>}
      </PlainNav>
    </div>
  )
}

export default PoorNavigation
